import { useEffect, useMemo, useState } from "react";
import { useStore } from "../state/store";
import { ensembleSets } from "../engine/liveGuidance";
import {
  BREAKDOWN_HOURS, coastIndex, ensembleBreakdown, median, REGIONS, type CoastIndex, type Region, type SourceBreakdown,
} from "../engine/ensembleBreakdown";
import { loadCoastline } from "../data/replayApi";
import { ENSEMBLE_SOURCES, ensembleName, type StormBundle } from "../data/liveApi";

// The coastline index is built once for the session (1.3k points)
let coastPromise: Promise<CoastIndex> | null = null;
const getCoast = () => (coastPromise ??= loadCoastline().then(coastIndex));

// Share of members → cell shade: one hue (blue), dim near zero, bright near
// all; white text on the dark steps, ink on the light ones
const STEPS: [number, string, string][] = [
  [5, "#104281", "#ffffff"], [15, "#184f95", "#ffffff"], [30, "#256abf", "#ffffff"], [50, "#3987e5", "#0b1220"],
  [70, "#6da7ec", "#0b1220"], [90, "#9ec5f4", "#0b1220"], [101, "#cde2fb", "#0b1220"],
];
const shade = (pct: number) => STEPS.find(([max]) => pct < max) ?? STEPS[STEPS.length - 1];
const pctText = (k: number, n: number) => {
  const p = (100 * k) / n;
  return k === 0 ? "–" : p < 1 ? "<1%" : `${Math.round(p)}%`;
};
// below this many members in all, a lack of US landfalls says nothing yet
const MIN_FOR_NO_LANDFALL = 20;
const runHour = (iso: string) => `${String(new Date(/[zZ]$/.test(iso) ? iso : `${iso}Z`).getUTCHours()).padStart(2, "0")}Z`;
const days = (h: number) => (h / 24 < 1 ? `+${Math.round(h)} h` : `day ${(h / 24).toFixed(1)}`);

/**
 * Per ensemble (columns): how many of its members develop the system, how
 * strong they get, and where they first make landfall on the US coast — each
 * as a share of that ensemble's members. A column header shows or hides that
 * ensemble's members on the map.
 */
export default function EnsembleBreakdown({ bundle }: { bundle: StormBundle }) {
  const shown = useStore((s) => s.live.shownEnsembles);
  const toggle = useStore((s) => s.toggleEnsembleSource);
  const [coast, setCoast] = useState<CoastIndex | null>(null);
  const [hover, setHover] = useState<string | null>(null); // the readout for the cell under the pointer
  useEffect(() => {
    let live = true;
    getCoast().then((c) => { if (live) setCoast(c); }).catch(() => { /* no landfall rows */ });
    return () => { live = false; };
  }, []);

  const rows = useMemo(() => {
    const order = ENSEMBLE_SOURCES.map((s) => s.source);
    const sets = ensembleSets(bundle).filter((s) => s.members.length > 0)
      .sort((a, b) => order.indexOf(a.source) - order.indexOf(b.source));
    return ensembleBreakdown(sets, coast);
  }, [bundle, coast]);

  const total = rows.reduce((n, r) => n + r.members, 0);
  const formed = rows.reduce((n, r) => n + r.formed, 0);
  const byFormation = rows.filter((r) => r.byFormation);
  const head = (
    <div className="muted xs" style={{ marginTop: 14 }}>
      🎲 ENSEMBLE BREAKDOWN
      {rows.length > 0 && (
        <span style={{ textTransform: "none" }}>
          {" · "}{byFormation.length ? `${formed.toLocaleString()} of ${total.toLocaleString()} members form it` : `${total.toLocaleString()} members`}
        </span>
      )}
    </div>
  );
  if (rows.length === 0) {
    return <>{head}<div className="muted xs" style={{ marginTop: 4 }}>No ensemble members for this system yet — they arrive as each model's tracker picks it up.</div></>;
  }

  const current = bundle.best_track[bundle.best_track.length - 1]?.vmax ?? 0;
  const deepmind = ensembleSets(bundle).find((s) => s.attribution);
  const regions = REGIONS.filter((r) => rows.some((b) => b.landfall[r]));
  const anyLandfall = regions.length > 0;
  const name = (src: string) => ensembleName(src).replace("ECMWF ENS", "ECMWF");

  const cell = (b: SourceBreakdown, k: number, detail: string) => {
    const [, bg, ink] = shade((100 * k) / b.members);
    const info = `${ensembleName(b.source)}: ${k.toLocaleString()} of ${b.members.toLocaleString()} members (${pctText(k, b.members)}) ${detail}`;
    return (
      <td
        key={b.source} tabIndex={0} className="eb-cell"
        style={k > 0 ? { background: bg, color: ink } : undefined}
        onMouseEnter={() => setHover(info)} onFocus={() => setHover(info)}
        onMouseLeave={() => setHover(null)} onBlur={() => setHover(null)}
      >
        {pctText(k, b.members)}
      </td>
    );
  };
  const strengthRow = (label: string, pick: (b: SourceBreakdown) => number, detail: string) => (
    <tr key={label}>
      <th scope="row">{label}</th>
      {rows.map((b) => cell(b, pick(b), detail))}
    </tr>
  );

  return (
    <>
      {head}
      <table className="eb-table">
        <thead>
          <tr>
            <th />
            {rows.map((b) => {
              const color = ENSEMBLE_SOURCES.find((s) => s.source === b.source)?.color ?? "#94A3B8";
              const on = shown.includes(b.source);
              return (
                <th key={b.source} scope="col">
                  <button
                    className={on ? "eb-src on" : "eb-src"} onClick={() => toggle(b.source)}
                    title={`${ensembleName(b.source)} ${runHour(b.init)} run · ${on ? "hide" : "show"} its ${b.formed.toLocaleString()} members${b.byFormation ? " that form it" : ""} on the map`}
                  >
                    <span className="eb-key" style={{ background: color }} />
                    {name(b.source)}
                    <span className="eb-run">{runHour(b.init)} · {b.members.toLocaleString()}</span>
                  </button>
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {current < 34 && byFormation.length > 0 && strengthRow("Form the low", (b) => b.formed, "form this low (in the first 5 days)")}
          {current < 34 && strengthRow("Develop (TS+)", (b) => b.ts, "reach tropical-storm strength (34 kt+)")}
          {strengthRow("Hurricane", (b) => b.hurricane, "become hurricanes (64 kt+)")}
          {strengthRow("Major (Cat 3+)", (b) => b.major, "become major hurricanes (96 kt+)")}
          {coast && (
            <tr className="eb-sep">
              <th scope="rowgroup" colSpan={rows.length + 1}>
                {anyLandfall
                  ? "First US landfall"
                  : formed < MIN_FOR_NO_LANDFALL
                    ? "US landfall: too few members so far to say"
                    : "No member reaches the US Gulf or East Coast"}
                <span className="eb-run"> · first {BREAKDOWN_HOURS / 24} days</span>
              </th>
            </tr>
          )}
          {regions.map((r: Region) => (
            <tr key={r}>
              <th scope="row">{r}</th>
              {rows.map((b) => {
                const rc = b.landfall[r];
                const k = rc?.n ?? 0;
                const when = rc ? median(rc.taus) : null;
                return cell(b, k, k
                  ? `make landfall first in ${r} — ${rc!.hurricanes.toLocaleString()} as hurricanes${when != null ? `, typically ${days(when)}` : ""}`
                  : `make landfall first in ${r}`);
              })}
            </tr>
          ))}
          {anyLandfall && (
            <tr>
              <th scope="row">No US landfall</th>
              {rows.map((b) => cell(b, b.noLandfall, `stay off the US coast for the first ${BREAKDOWN_HOURS / 24} days`))}
            </tr>
          )}
        </tbody>
      </table>
      <div className="muted xs eb-readout" aria-live="polite">
        {hover ?? "Hover a cell for member counts · click a model to show its members on the map."}
      </div>
      {byFormation.length > 0 && (
        <div className="muted xs eb-note">
          {byFormation.map((b) => name(b.source)).join(", ")} {byFormation.length > 1 ? "don't" : "doesn't"} track this
          system by name yet (that starts with the next model runs), so a member counts when its own low forms inside
          NHC's outlook area or near the system within 5 days. Members that never form it count as no development
          and no landfall.
        </div>
      )}
      <div className="muted xs eb-note">
        Each member counted once: its strongest point and its first US landfall in the first {BREAKDOWN_HOURS / 24} days
        of its run. GEFS, ECMWF and AIFS run coarse and usually underplay a strong storm's winds; DeepMind's model is
        trained on tropical cyclones. Scenarios, not the official forecast.
      </div>
      {deepmind?.attribution && (
        <div className="muted xs eb-note">
          DeepMind: {deepmind.attribution}{" "}
          {deepmind.terms_url && <a href={deepmind.terms_url} target="_blank" rel="noreferrer">terms ↗</a>}
        </div>
      )}
    </>
  );
}
